import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { Link } from "react-router-dom";
import {
  FileText, CalendarBlank, Factory, Scales, ShieldStar,
  CheckCircle, Clock, XCircle, Question, ArrowRight,
  TrendUp, Warning, Lightning
} from "@phosphor-icons/react";
import ExportButton from "../components/ExportButton";

const ACCENT = {
  blue:    { bg:"bg-blue-50",    text:"text-blue-700",    bar:"bg-blue-500",    ring:"hover:ring-blue-200" },
  emerald: { bg:"bg-emerald-50", text:"text-emerald-700", bar:"bg-emerald-500", ring:"hover:ring-emerald-200" },
  amber:   { bg:"bg-amber-50",   text:"text-amber-700",   bar:"bg-amber-500",   ring:"hover:ring-amber-200" },
  rose:    { bg:"bg-rose-50",    text:"text-rose-700",    bar:"bg-rose-500",    ring:"hover:ring-rose-200" },
  orange:  { bg:"bg-orange-50",  text:"text-orange-700",  bar:"bg-orange-500",  ring:"hover:ring-orange-200" },
  indigo:  { bg:"bg-indigo-50",  text:"text-indigo-700",  bar:"bg-indigo-500",  ring:"hover:ring-indigo-200" },
  neutral: { bg:"bg-neutral-100",text:"text-neutral-600", bar:"bg-neutral-400", ring:"hover:ring-neutral-200" },
};

const Card = ({ title, value, icon: Icon, accent="blue", to, sub, testid }) => {
  const c = ACCENT[accent];
  return (
    <Link to={to} data-testid={testid}
      className={`group relative bg-white border border-neutral-200 rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-neutral-900/5 hover:border-neutral-300 hover:ring-4 ${c.ring} block`}>
      <div className={`absolute top-0 left-0 right-0 h-1 ${c.bar} rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity`}></div>
      <div className="flex items-start justify-between mb-3">
        <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{title}</span>
        <div className={`w-10 h-10 rounded-lg ${c.bg} ${c.text} flex items-center justify-center group-hover:scale-110 transition-transform`}>
          <Icon size={18} weight="duotone"/>
        </div>
      </div>
      <div className="font-display text-3xl lg:text-4xl font-black tracking-tighter text-neutral-900">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1.5 flex items-center gap-1">{sub}</div>}
      <ArrowRight size={14} className="absolute bottom-4 right-4 text-neutral-300 group-hover:text-neutral-700 group-hover:translate-x-1 transition-all"/>
    </Link>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [compliance, setCompliance] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(r=>setStats(r.data)).catch(()=>{});
    api.get("/calendar/events").then(r=>setEvents(r.data.slice(0,6))).catch(()=>{});
    api.get("/compliance/check").then(r=>setCompliance(r.data)).catch(()=>{});
  }, []);

  const total = stats?.total_documents || 0;
  const pct = (v) => total>0 ? Math.round((v/total)*100) : 0;
  const score = compliance?.score ?? 0;
  const scoreColor = score >= 80 ? "from-emerald-500 to-emerald-600" : score >= 60 ? "from-amber-500 to-orange-500" : "from-rose-500 to-rose-600";

  return (
    <div className="space-y-7" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Centro de monitoreo</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-neutral-900">Hola, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-neutral-500 mt-2 max-w-2xl">Resumen ejecutivo del cumplimiento documental y operativo.</p>
        </div>
        <ExportButton resource="dashboard" label="Exportar reporte"/>
      </div>

      {/* Executive KPI strip */}
      <div className="bg-gradient-to-br from-neutral-700 to-neutral-600 text-white rounded-2xl p-6 lg:p-7" data-testid="kpi-strip">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
          <div className="lg:pr-6">
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 flex items-center gap-1.5"><ShieldStar size={12}/> Cumplimiento</div>
            <div className={`font-display text-4xl font-black tracking-tighter bg-gradient-to-br ${scoreColor} bg-clip-text text-transparent`} data-testid="kpi-compliance">{score}%</div>
            <div className="text-xs text-white/50 mt-1">{compliance?.covered||0}/{compliance?.total_obligations||0} obligaciones</div>
          </div>
          <div className="lg:px-6 pt-4 lg:pt-0">
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 flex items-center gap-1.5"><Warning size={12}/> Riesgo alto</div>
            <div className="font-display text-4xl font-black tracking-tighter text-rose-400" data-testid="kpi-risk">{compliance?.risk_high||0}</div>
            <div className="text-xs text-white/50 mt-1">Acción inmediata</div>
          </div>
          <div className="lg:px-6 pt-4 lg:pt-0">
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 flex items-center gap-1.5"><Clock size={12}/> Próximos 30d</div>
            <div className="font-display text-4xl font-black tracking-tighter text-amber-300" data-testid="kpi-proximos">{stats?.docs_proximos ?? 0}</div>
            <div className="text-xs text-white/50 mt-1">Documentos por vencer</div>
          </div>
          <div className="lg:pl-6 pt-4 lg:pt-0">
            <div className="text-[11px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 flex items-center gap-1.5"><Lightning size={12}/> Activos</div>
            <div className="font-display text-4xl font-black tracking-tighter text-emerald-300" data-testid="kpi-vigentes">{stats?.docs_vigentes ?? 0}</div>
            <div className="text-xs text-white/50 mt-1">Vigentes hoy</div>
          </div>
        </div>
      </div>

      {/* Main KPI cards (clickable, semantic) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Card testid="stat-total-documents" title="Documentos" value={stats?.total_documents ?? "—"} icon={FileText} accent="blue" to="/documentos"/>
        <Card testid="stat-installations" title="Instalaciones" value={stats?.total_installations ?? "—"} icon={Factory} accent="indigo" to="/instalaciones"/>
        <Card testid="stat-obligations" title="Obligaciones" value={stats?.total_obligations ?? "—"} icon={Scales} accent="emerald" to="/obligaciones"/>
        <Card testid="stat-events" title="Eventos calendario" value={stats?.total_events ?? "—"} icon={CalendarBlank} accent="orange" to="/calendario"/>
        <Card testid="stat-vigentes" title="Vigentes" value={stats?.docs_vigentes ?? "—"} icon={CheckCircle} accent="emerald" to="/documentos"/>
        <Card testid="stat-proximos" title="Próximos a vencer" value={stats?.docs_proximos ?? "—"} icon={Clock} accent="amber" to="/cumplimiento"/>
        <Card testid="stat-vencidos" title="Vencidos" value={stats?.docs_vencidos ?? "—"} icon={XCircle} accent="rose" to="/cumplimiento"/>
        <Card testid="stat-pendientes" title="Pendientes" value={stats?.docs_pendientes ?? "—"} icon={Question} accent="neutral" to="/documentos"/>
      </div>

      {/* Two-column: upcoming + compliance breakdown */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-xl p-6" data-testid="upcoming-panel">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Próximos vencimientos</h2>
              <p className="text-sm text-neutral-500">Eventos críticos y preventivos.</p>
            </div>
            <Link to="/calendario" className="text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">Ver calendario <ArrowRight size={14}/></Link>
          </div>
          {events.length === 0 ? (
            <div className="py-12 text-center text-neutral-400 text-sm">Sin eventos próximos.</div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {events.map(ev=>{
                const days = Math.ceil((new Date(ev.event_date)-new Date())/86400000);
                const isCritical = days < 0 || ev.alarm_level==="critica" || ev.alarm_level==="vencimiento";
                const isPreventive = days >= 0 && days <= 7;
                const ac = isCritical ? ACCENT.rose : isPreventive ? ACCENT.amber : ACCENT.emerald;
                const label = days < 0 ? `Vencido ${Math.abs(days)}d` : `En ${days}d`;
                return (
                  <li key={ev.id} className="py-3.5 flex items-center gap-4 group" data-testid={`upcoming-${ev.id}`}>
                    <span className={`w-1 h-12 rounded-full ${ac.bar}`}></span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-neutral-900 truncate group-hover:text-blue-700 transition-colors">{ev.title}</div>
                      <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-2">
                        <span>{ev.event_date}</span>
                        {ev.alarm_level && <><span>·</span><span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold ${ac.bg} ${ac.text}`}>{ev.alarm_level}</span></>}
                      </div>
                    </div>
                    {isCritical && <span className="px-2.5 py-1 bg-rose-50 text-rose-700 border border-rose-200 rounded-md text-xs font-bold uppercase tracking-wide flex items-center gap-1"><Warning size={12} weight="bold"/> Crítico</span>}
                    {!isCritical && isPreventive && <span className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-md text-xs font-bold uppercase tracking-wide">Preventivo</span>}
                    <span className={`text-xs font-bold tabular-nums ${ac.text} min-w-[80px] text-right`}>{label}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-6" data-testid="status-breakdown">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Distribución</h2>
              <p className="text-sm text-neutral-500">Por estatus documental.</p>
            </div>
            <Link to="/cumplimiento" className="text-blue-600 hover:text-blue-700"><TrendUp size={18}/></Link>
          </div>
          <div className="space-y-4">
            {[
              {k:"docs_vigentes",l:"Vigentes",c:"emerald"},
              {k:"docs_proximos",l:"Próximos",c:"amber"},
              {k:"docs_vencidos",l:"Vencidos",c:"rose"},
              {k:"docs_pendientes",l:"Pendientes",c:"neutral"},
            ].map(r=>{
              const v = stats?.[r.k]||0; const p = pct(v); const c = ACCENT[r.c];
              return (
                <div key={r.k}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="font-medium text-neutral-700">{r.l}</span>
                    <span className="text-neutral-500 tabular-nums">{v} <span className="text-xs">({p}%)</span></span>
                  </div>
                  <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                    <div className={`h-full ${c.bar} rounded-full transition-all duration-700`} style={{width:`${p}%`}}></div>
                  </div>
                </div>
              );
            })}
          </div>
          <Link to="/cumplimiento" className="mt-6 block text-center py-2.5 bg-neutral-700 hover:bg-neutral-600 text-white text-sm font-semibold rounded-lg transition-colors" data-testid="goto-compliance">
            Ver análisis completo →
          </Link>
        </div>
      </div>
    </div>
  );
}
